import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Phone, PhoneCall, PhoneOff, PhoneMissed, Clock, PlayCircle, Settings, RefreshCw, ChevronDown, ChevronUp, Tag, Calendar, MessageSquare } from 'lucide-react';

const DISPOSITION_OPTIONS = [
  { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-700' },
  { value: 'not_interested', label: 'Not Interested', color: 'bg-red-100 text-red-700' },
  { value: 'callback_requested', label: 'Callback Requested', color: 'bg-blue-100 text-blue-700' },
  { value: 'wrong_number', label: 'Wrong Number', color: 'bg-gray-100 text-gray-700' },
  { value: 'not_reachable', label: 'Not Reachable', color: 'bg-orange-100 text-orange-700' },
  { value: 'voicemail', label: 'Voicemail', color: 'bg-purple-100 text-purple-700' },
  { value: 'no_answer', label: 'No Answer', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'busy', label: 'Busy', color: 'bg-orange-100 text-orange-700' },
  { value: 'dnd', label: 'DND', color: 'bg-red-100 text-red-700' },
  { value: 'follow_up', label: 'Follow Up Required', color: 'bg-blue-100 text-blue-700' },
  { value: 'qualified', label: 'Qualified', color: 'bg-green-100 text-green-700' },
  { value: 'not_qualified', label: 'Not Qualified', color: 'bg-red-100 text-red-700' },
  { value: 'meeting_scheduled', label: 'Meeting Scheduled', color: 'bg-green-100 text-green-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' },
];

function CallsTab() {
  const [leads, setLeads] = useState([]);
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [calling, setCalling] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [telephonyEnabled, setTelephonyEnabled] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [expandedCall, setExpandedCall] = useState(null);
  const [editingCall, setEditingCall] = useState(null);
  
  // Call form
  const [callForm, setCallForm] = useState({
    lead_id: '',
    lead_phone: '',
    lead_name: '',
    agent_phone: '',
  });

  // Telephony settings
  const [telephonySettings, setTelephonySettings] = useState({
    enabled: false,
    provider: 'custom',
    api_url: '',
    api_key: '',
    webhook_url: '',
    default_caller_id: '',
    default_agent_phone: '',
    initiate_endpoint: '/click-to-call',
    initiate_method: 'POST',
    initiate_headers: '{}',
    initiate_body_template: JSON.stringify({
      from: '{{agent_phone}}',
      to: '{{lead_phone}}',
      caller_id: '{{caller_id}}',
      callback_url: '{{webhook_url}}',
      call_id: '{{call_id}}'
    }, null, 2),
    field_mappings: JSON.stringify({
      provider_call_id: 'call_sid',
      status: 'status',
      duration: 'duration',
      recording_url: 'recording_url'
    }, null, 2),
    auto_record: true
  });

  const fetchLeads = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data.filter(l => l.phone));
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }, []);

  const fetchCalls = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calls?limit=50`);
      setCalls(response.data.calls || []);
    } catch (error) {
      console.error('Error fetching calls:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calls/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching call stats:', error);
    }
  }, []);

  const fetchTelephonySettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calls/telephony/settings`);
      const data = response.data;
      setTelephonyEnabled(data.enabled || false);
      setTelephonySettings({
        ...data,
        initiate_headers: typeof data.initiate_headers === 'object' 
          ? JSON.stringify(data.initiate_headers, null, 2) 
          : data.initiate_headers || '{}',
        initiate_body_template: typeof data.initiate_body_template === 'object'
          ? JSON.stringify(data.initiate_body_template, null, 2)
          : data.initiate_body_template || '{}',
        field_mappings: typeof data.field_mappings === 'object'
          ? JSON.stringify(data.field_mappings, null, 2)
          : data.field_mappings || '{}'
      });
    } catch (error) {
      console.error('Error fetching telephony settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchCalls(), fetchStats(), fetchTelephonySettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads, fetchCalls, fetchStats, fetchTelephonySettings]);

  const handleLeadSelect = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    if (lead) {
      setCallForm({
        ...callForm,
        lead_id: leadId,
        lead_phone: lead.phone || '',
        lead_name: lead.name || '',
      });
    }
  };

  const initiateCall = async (e) => {
    e.preventDefault();
    if (!callForm.lead_id || !callForm.lead_phone) {
      setMessage({ type: 'error', text: 'Please select a lead with a phone number' });
      return;
    }

    setCalling(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.post(`${API}/calls/initiate`, {
        lead_id: callForm.lead_id,
        lead_phone: callForm.lead_phone,
        lead_name: callForm.lead_name,
        agent_phone: callForm.agent_phone || null,
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.message || 'Call initiated! Your phone will ring shortly.' });
        setCallForm({ lead_id: '', lead_phone: '', lead_name: '', agent_phone: '' });
        // Refresh calls list
        setTimeout(fetchCalls, 2000);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Failed to initiate call' });
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to initiate call. Check telephony settings.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setCalling(false);
    }
  };

  const saveTelephonySettings = async () => {
    try {
      // Parse JSON strings before sending
      const settingsToSave = {
        ...telephonySettings,
        initiate_headers: JSON.parse(telephonySettings.initiate_headers || '{}'),
        initiate_body_template: JSON.parse(telephonySettings.initiate_body_template || '{}'),
        field_mappings: JSON.parse(telephonySettings.field_mappings || '{}')
      };

      await axios.post(`${API}/calls/telephony/settings`, settingsToSave);
      setMessage({ type: 'success', text: 'Telephony settings saved successfully!' });
      setTelephonyEnabled(settingsToSave.enabled);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.detail || 'Failed to save settings' });
    }
  };

  const updateCallDisposition = async (callId, disposition, notes, tags, followUpDate) => {
    try {
      await axios.put(`${API}/calls/${callId}`, {
        disposition,
        notes,
        tags,
        follow_up_date: followUpDate
      });
      setMessage({ type: 'success', text: 'Call updated successfully!' });
      fetchCalls();
      setEditingCall(null);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update call' });
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <PhoneCall className="w-4 h-4 text-green-600" />;
      case 'missed': return <PhoneMissed className="w-4 h-4 text-yellow-600" />;
      case 'failed': return <PhoneOff className="w-4 h-4 text-red-600" />;
      case 'ringing':
      case 'in_progress': return <Phone className="w-4 h-4 text-blue-600 animate-pulse" />;
      default: return <Phone className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-700',
      missed: 'bg-yellow-100 text-yellow-700',
      failed: 'bg-red-100 text-red-700',
      ringing: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-blue-100 text-blue-700',
      initiated: 'bg-gray-100 text-gray-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  const getDispositionBadge = (disposition) => {
    const option = DISPOSITION_OPTIONS.find(d => d.value === disposition);
    return option?.color || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calls-tab">
      {/* Stats Summary */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">Total Calls</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white">{stats.total_calls}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">Completed</p>
            <p className="text-2xl font-semibold text-green-600">{stats.completed_calls}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">Missed</p>
            <p className="text-2xl font-semibold text-yellow-600">{stats.missed_calls}</p>
          </div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">Answer Rate</p>
            <p className="text-2xl font-semibold text-blue-600">{stats.answer_rate}%</p>
          </div>
          <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
            <p className="text-sm text-slate-500 dark:text-slate-400">Avg Duration</p>
            <p className="text-2xl font-semibold text-slate-900 dark:text-white">{formatDuration(Math.round(stats.avg_duration))}</p>
          </div>
        </div>
      )}

      {/* Message */}
      {message.text && (
        <div className={`px-4 py-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 border border-green-200 text-green-700' 
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Settings Toggle */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-white"
        >
          <Settings className="w-4 h-4" />
          {showSettings ? 'Hide Settings' : 'Configure Telephony'}
        </button>
      </div>

      {/* Telephony Settings Panel */}
      {showSettings && (
        <div className="bg-slate-50 dark:bg-slate-900 p-6 rounded-lg border border-slate-200 dark:border-slate-700 space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Telephony Provider Settings</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">Configure your cloud telephony provider (Twilio, Exotel, intalk.io, or any custom provider)</p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="telephony-enabled"
                checked={telephonySettings.enabled}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, enabled: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
              />
              <label htmlFor="telephony-enabled" className="text-sm font-medium text-slate-700 dark:text-slate-300">Enable Telephony</label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Provider</label>
              <select
                value={telephonySettings.provider}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, provider: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
              >
                <option value="custom">Custom / Generic Webhook</option>
                <option value="twilio">Twilio</option>
                <option value="exotel">Exotel</option>
                <option value="intalk">intalk.io</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">API Base URL</label>
              <input
                type="text"
                value={telephonySettings.api_url}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, api_url: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                placeholder="https://api.yourprovider.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">API Key / Auth Token</label>
              <input
                type="password"
                value={telephonySettings.api_key}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, api_key: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                placeholder="Your API key"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Default Caller ID</label>
              <input
                type="text"
                value={telephonySettings.default_caller_id}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, default_caller_id: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                placeholder="+91XXXXXXXXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Your Phone Number (Agent)</label>
              <input
                type="text"
                value={telephonySettings.default_agent_phone}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, default_agent_phone: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                placeholder="+91XXXXXXXXXX"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Initiate Endpoint</label>
              <input
                type="text"
                value={telephonySettings.initiate_endpoint}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, initiate_endpoint: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
                placeholder="/click-to-call"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Webhook URL (for provider callbacks)</label>
              <input
                type="text"
                value={telephonySettings.webhook_url}
                onChange={(e) => setTelephonySettings({ ...telephonySettings, webhook_url: e.target.value })}
                className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm bg-slate-100"
                placeholder="Auto-generated"
                readOnly
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Request Body Template (JSON)</label>
            <textarea
              value={telephonySettings.initiate_body_template}
              onChange={(e) => setTelephonySettings({ ...telephonySettings, initiate_body_template: e.target.value })}
              rows="6"
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-mono"
              placeholder='{"from": "{{agent_phone}}", "to": "{{lead_phone}}"}'
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Available placeholders: {'{{agent_phone}}'}, {'{{lead_phone}}'}, {'{{lead_name}}'}, {'{{caller_id}}'}, {'{{webhook_url}}'}, {'{{call_id}}'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Field Mappings (JSON)</label>
            <textarea
              value={telephonySettings.field_mappings}
              onChange={(e) => setTelephonySettings({ ...telephonySettings, field_mappings: e.target.value })}
              rows="4"
              className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm font-mono"
            />
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Map your provider's response fields to: provider_call_id, status, duration, recording_url, start_time, end_time
            </p>
          </div>

          <div className="flex justify-end">
            <button
              onClick={saveTelephonySettings}
              className="px-4 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition"
            >
              Save Settings
            </button>
          </div>
        </div>
      )}

      {/* Click to Call Form */}
      {telephonyEnabled ? (
        <div className="bg-white dark:bg-slate-800 p-6 rounded-lg border border-slate-200 dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Click to Call</h3>
          <form onSubmit={initiateCall} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Select Lead *</label>
                <select
                  value={callForm.lead_id}
                  onChange={(e) => handleLeadSelect(e.target.value)}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg"
                  data-testid="call-lead-select"
                >
                  <option value="">Choose a lead with phone...</option>
                  {leads.map((lead) => (
                    <option key={lead.id} value={lead.id}>
                      {lead.name} ({lead.phone})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone Number</label>
                <input
                  type="text"
                  value={callForm.lead_phone}
                  onChange={(e) => setCallForm({ ...callForm, lead_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg"
                  placeholder="+91XXXXXXXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Your Phone (optional)</label>
                <input
                  type="text"
                  value={callForm.agent_phone}
                  onChange={(e) => setCallForm({ ...callForm, agent_phone: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg"
                  placeholder="Uses default if empty"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={calling || !callForm.lead_phone}
                className="flex items-center gap-2 px-6 py-2 bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg hover:bg-slate-800 dark:hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition"
                data-testid="initiate-call-btn"
              >
                {calling ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Calling...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4" />
                    Call Now
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <p className="text-yellow-800">
            <strong>Telephony not configured.</strong> Click "Configure Telephony" above to set up your cloud telephony provider.
          </p>
        </div>
      )}

      {/* Call History */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Recent Calls</h3>
          <button
            onClick={() => { fetchCalls(); fetchStats(); }}
            className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="divide-y divide-slate-100">
          {calls.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <Phone className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No calls yet. Start by making your first call!</p>
            </div>
          ) : (
            calls.map((call) => (
              <div key={call.id} className="p-4 hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900 transition">
                <div 
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setExpandedCall(expandedCall === call.id ? null : call.id)}
                >
                  <div className="flex items-center gap-4">
                    {getStatusIcon(call.status)}
                    <div>
                      <p className="font-medium text-slate-900 dark:text-white">{call.lead_name || 'Unknown'}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{call.lead_phone}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(call.status)}`}>
                      {call.status}
                    </span>
                    {call.disposition && (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getDispositionBadge(call.disposition)}`}>
                        {DISPOSITION_OPTIONS.find(d => d.value === call.disposition)?.label || call.disposition}
                      </span>
                    )}
                    <div className="text-right">
                      <p className="text-sm text-slate-900 dark:text-white">{formatDuration(call.duration)}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">{formatDateTime(call.initiated_at)}</p>
                    </div>
                    {call.recording_url && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(call.recording_url, '_blank');
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                        title="Play Recording"
                      >
                        <PlayCircle className="w-5 h-5" />
                      </button>
                    )}
                    {expandedCall === call.id ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>

                {/* Expanded Call Details */}
                {expandedCall === call.id && (
                  <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Direction</p>
                        <p className="font-medium">{call.direction === 'outbound' ? '↗️ Outbound' : '↙️ Inbound'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Agent</p>
                        <p className="font-medium">{call.agent_name || call.agent_phone}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Started</p>
                        <p className="font-medium">{formatDateTime(call.answered_at) || '-'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 dark:text-slate-400">Ended</p>
                        <p className="font-medium">{formatDateTime(call.ended_at) || '-'}</p>
                      </div>
                    </div>

                    {call.recording_url && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Recording</p>
                        <audio controls className="w-full" src={call.recording_url}>
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    )}

                    {/* Disposition & Notes Form */}
                    {editingCall === call.id ? (
                      <CallEditForm
                        call={call}
                        onSave={(disposition, notes, tags, followUp) => updateCallDisposition(call.id, disposition, notes, tags, followUp)}
                        onCancel={() => setEditingCall(null)}
                      />
                    ) : (
                      <div className="flex items-center gap-4">
                        {call.notes && (
                          <p className="text-sm text-slate-600 dark:text-slate-400 flex-1">
                            <MessageSquare className="w-4 h-4 inline mr-1" />
                            {call.notes}
                          </p>
                        )}
                        <button
                          onClick={() => setEditingCall(call.id)}
                          className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded"
                        >
                          {call.disposition ? 'Edit' : 'Add Disposition'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Sub-component for editing call disposition
function CallEditForm({ call, onSave, onCancel }) {
  const [disposition, setDisposition] = useState(call.disposition || '');
  const [notes, setNotes] = useState(call.notes || '');
  const [tags, setTags] = useState(call.tags?.join(', ') || '');
  const [followUp, setFollowUp] = useState(call.follow_up_date || '');

  return (
    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Disposition</label>
          <select
            value={disposition}
            onChange={(e) => setDisposition(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
          >
            <option value="">Select disposition...</option>
            {DISPOSITION_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Follow-up Date</label>
          <input
            type="datetime-local"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Tags (comma separated)</label>
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
          placeholder="hot lead, decision maker, follow-up"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows="2"
          className="w-full px-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm"
          placeholder="Call summary, next steps..."
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-200 rounded"
        >
          Cancel
        </button>
        <button
          onClick={() => onSave(disposition, notes, tags.split(',').map(t => t.trim()).filter(Boolean), followUp)}
          className="px-3 py-1.5 text-sm bg-slate-900 text-white rounded hover:bg-slate-800"
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default CallsTab;
